"use client";

import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import type {
  IResource,
  ISchedule,
  WorkplaceRequestElement
} from "@gettactic/api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@gettactic/components/src/primitives/tabs";
import { getScheduleAttendeeSlots } from "@gettactic/helpers/src/schedules/getScheduleAttendees";
import React from "react";
import { WPRequestsList } from "./WPRequestsList";

export interface TimelineRoomDataProps {
  schedule: ISchedule;
  resource: IResource | undefined;
  workplaceRequests: WorkplaceRequestElement[];
}

/**
 * A room booking's tabs as the resource schedule shows them: who is coming as
 * a grid of cards, the room's amenities as pills, and its requests — with the
 * tab there even when there are none, so it reads the same on every booking.
 */
export const TimelineRoomData: React.FC<TimelineRoomDataProps> = ({
  schedule,
  resource,
  workplaceRequests
}) => {
  // One slot per attendee: a reservation holding several rooms repeats every
  // attendee once per room.
  const attendeeSlots = getScheduleAttendeeSlots(schedule);
  const amenities = resource?.tags ?? [];

  return (
    <Tabs defaultValue="attendees">
      <TabsList className="px-2.5 md:px-0">
        <TabsTrigger value="attendees">
          Attendees ({attendeeSlots.length})
        </TabsTrigger>
        <TabsTrigger value="amenities">Amenities</TabsTrigger>
        <TabsTrigger value="requests">
          Requests ({workplaceRequests.length})
        </TabsTrigger>
      </TabsList>

      <TabsContent value="attendees" className="px-2.5 pt-4 md:px-0">
        <div className="grid grid-cols-3 gap-2.5">
          {attendeeSlots.map((slot) => (
            <div
              key={slot.user.id ?? slot.user.email}
              className="flex min-w-0 items-center gap-2.5 rounded-brand border border-neutral-100 px-3 py-2.5"
            >
              <span className="block size-8 flex-shrink-0 text-xs">
                <UserAvatar user={slot.user} size="full" />
              </span>
              <span className="min-w-0 truncate text-content-strong text-row-title">
                {slot.user.name || slot.user.email}
              </span>
            </div>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="amenities" className="px-2.5 pt-4 md:px-0">
        {amenities.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {amenities.map((amenity) => (
              <span
                key={amenity.id}
                className="flex h-7.5 items-center rounded-full bg-neutral-50 px-3 font-medium text-cell text-content-strong"
              >
                {amenity.name}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-cell text-content">No amenities listed yet</p>
        )}
      </TabsContent>

      <TabsContent value="requests" className="px-2.5 pt-4 md:px-0">
        {workplaceRequests.length > 0 ? (
          <WPRequestsList
            schedule={schedule}
            resource={resource}
            workplaceRequests={workplaceRequests}
          />
        ) : (
          <div className="rounded-brand border border-neutral-100 border-dashed p-6 text-center">
            <p className="font-semibold text-content-strong text-row-title">
              No requests on this reservation
            </p>
            <p className="mt-1 text-cell text-content">
              Attach a workplace request for setup, catering, AV, or
              maintenance.
            </p>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};
